import { getEscHandler } from "@/app/esc_handler"
import { useLockedBody } from "@/app/hooks/locked_body"
import { mainLogger as log } from "@/app/logger"
import { isFunction, isNotNull, isNull, isStringNotEmpty } from "@/app/utils"
import { useRouter } from "next/router"
import React, {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react"

/*
 * The provider exports a <TrayProvider /> that wraps over children components that intend
 * to use the custom <Tray /> component for choices.
 *
 * The exported useTray() custom hook function returns reference to a function to show/hide
 * the selector, and a boolean on whether the tray is currently being shown.
 *
 */

interface TrayContextProps {
  showTray: (component: JSX.Element | null) => void
  isTrayShown: boolean
  activeTab: number
  setActiveTab: (index: number) => void
}

// setup a context to provide the showTray() method to all child components
// the showTray() method returned by this hook takes an object of type TrayProps
// call showTray(null) to hide the selector (if its being shown)
const ToggleTrayContext = createContext<TrayContextProps>({
  isTrayShown: false,
  activeTab: 0,
  setActiveTab: () => {},
  showTray: () => {},
})

// export hook function for children to access the showSelector() method
export function useTray() {
  const { activeTab, isTrayShown, setActiveTab, showTray } =
    useContext(ToggleTrayContext)

  return { showTray, isTrayShown, activeTab, setActiveTab } // Return active tab state and function
}

export function TrayProvider({ children }: { children: ReactNode }) {
  // to show the tray, invoke setTray() with a non-null object containing the options/callback
  // the setTray() is provided to all children components via the ToggleTrayContext below
  const [tray, setTray] = useState<JSX.Element | null>(null)
  const [activeTab, setActiveTab] = useState(0) // Add state for active tab
  const [isVisible, setIsVisible] = useState(false)

  const { pathname } = useRouter()
  const isChartsPage = pathname.startsWith("/chart")

  const isTrayShown = isNotNull(tray)

  const showTray = (tray: JSX.Element | null) => {
    if (tray) {
      setTray(tray)
      setTimeout(() => setIsVisible(true), 0)
    } else {
      setIsVisible(false)
      setTimeout(() => setTray(null), 300)
    }
  }

  // lock scrolling for <body> tag, if the tray is set
  const [_, setBodyLock] = useLockedBody(isNotNull(tray))

  // setup a function that will enable scrolling and
  // let the TrayProvider know that Tray needs to be closed (unmounted)
  const closeTray = useCallback(() => {
    log.debug("closing the tray")
    // enable scrolling on the <body> tag
    setBodyLock(false)
    showTray(null)
  }, [setTray, setBodyLock])

  // install a handler to close on "Esc" key, if the tray is set, otherwise empty function
  useEffect(getEscHandler(closeTray))

  // first wrap the children over the Context to pass them the setTray() method
  // then render the tray if any of the children have invoked setTray()
  return (
    <ToggleTrayContext.Provider
      value={{ activeTab, isTrayShown, setActiveTab, showTray }}>
      {children}
      {tray && (
        <>
          <div
            onClick={() => showTray(null)}
            className={`fixed inset-0 z-20 bg-main-700/50 transition-opacity duration-300 ${
              isVisible ? "opacity-100" : "opacity-0"
            }`}
          />
          <div
            className={`fixed top-[5vh] z-20 h-[90vh] w-[90vw] max-w-graf-tray transform transition-opacity duration-300 xl:top-[10vh] xl:h-[87vh] ${
              isVisible ? "opacity-100" : "opacity-0"
            } `}>
            {tray}
          </div>
        </>
      )}
    </ToggleTrayContext.Provider>
  )
}

// The Tray component renders the tray. It is invoked from the TrayProvider to render as a modal.
// The children can be JSX or a collection of TrayTab elements each with their own JSX.
// While rendering the tray component the client code can listen to TabChange event, or control
// the Tray by setting the optional activeTabId to the id of one of the child TrayTab.
type TrayProps = {
  activeTabId?: string
  onTabChange?: (newTabId: string) => void
  children: ReactNode
}

export function Tray({ activeTabId, onTabChange, children }: TrayProps) {
  const { activeTab, setActiveTab } = useTray() // Get active tab state and function

  // isolate Tray's childrens that are TrayTab with non-empty id and title
  // then map each TrayTab to TrayTabProps array
  const tabs: any[] = React.Children.toArray(children)
    .filter((c) => c?.type?.__isTrayTab) // check internal property
    .filter(
      (tab) =>
        isStringNotEmpty(tab.props?.id) && isStringNotEmpty(tab.props?.title)
    )
    .map((tab) => {
      log.trace("Got tab with props=", tab.props)
      return {
        id: tab.props?.id,
        title: tab.props?.title,
        children: tab.props?.children,
      }
    })

  // show tab headers only if there are 2+ <TrayTab> child elements
  // which means a single <TrayTab> will not show the tab headers
  const hasTabs = tabs.length > 1
  if (!hasTabs) {
    tabs.length = 0 // remove the single TrayTab element (if present)
    tabs.push({ id: "", title: "", children }) // add a placeholder with Tray's children
  }

  const initialTab: number =
    (
      !hasTabs ||
      isNull(activeTabId) ||
      tabs.findIndex((tab) => tab.id == activeTabId) < 0
    ) ?
      0
    : tabs.findIndex((tab) => tab.id == activeTabId)

  setActiveTab(initialTab) // set the initial tab

  // setup a new handler which will change the tab and invoke the callback if specified
  const tabChange = useCallback(
    (tabId: string) => {
      const newTab: number = tabs.findIndex((tab) => tab.id == tabId)
      if (newTab < 0) {
        log.error("Could not find tab with id", tabId, " ignoring tab change")
      } else if (newTab == activeTab) {
        log.trace("Ignoring click on active tab", tabId)
      } else {
        log.trace("Changing tray to tabId", tabId)
        setActiveTab(newTab)
        if (onTabChange && isFunction(onTabChange)) onTabChange(tabId)
      }
    },
    [tabs, setActiveTab, onTabChange]
  ) // the callback only changes if the handler changes or the tabs change

  return (
    <div className="flex h-full w-full flex-col justify-between">
      {/* if hasTabs, then render tab headers which are clickable to switch tabs */}
      {hasTabs && (
        <div id="tabs" className="flex h-fit w-fit max-w-full flex-row">
          {tabs.map((tab, tabIdx) => (
            <div
              key={tab.id}
              id={tab.id}
              onClick={() => tabChange(tab.id)}
              className={`block cursor-pointer rounded-tl-2xl rounded-tr-2xl text-center ${activeTab == tabIdx ? "bg-main-light" : "bg-main-300"} min-w-10 px-8 py-2`}>
              {tab.title}
            </div>
          ))}
        </div>
      )}
      {/* main Tab body, for the activeTab */}
      <div className="h-full w-full overflow-auto overscroll-contain rounded-br-2xl rounded-tr-2xl border-b-8 border-r-8 border-t-8 border-main-light bg-main-light">
        {tabs[activeTab]?.children}
      </div>
    </div>
  )
}
